import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useRef } from 'react';
import useAppToast from '@/hooks/useAppToast';
import { TENANT_ROUTES } from '@/constants/routes';
import { deleteConsent, type ApiResponse } from '@/services/main/consent';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import {
  CONSENT_DELETE_ERROR_FALLBACK,
  consentDeleteSuccessToastMessage,
} from '@/constants/consentForm';
import { invalidateVisitClinicScheduleForPatient } from './useVisitClinic';

/**
 * Invalidates all consent list queries for the patient (any filters / sort / serviceType).
 * Active queries refetch immediately after invalidation.
 */
function invalidateConsentListForPatient(
  queryClient: QueryClient,
  tenant: string,
  clinicNumber: string,
  patientId: string
) {
  return queryClient.invalidateQueries({
    queryKey: ['consents', 'list', tenant, clinicNumber, patientId],
    refetchType: 'active',
  });
}

function invalidateConsentDetailForPatient(
  queryClient: QueryClient,
  tenant: string,
  clinicNumber: string,
  patientId: string
) {
  return queryClient.invalidateQueries({
    queryKey: ['consents', 'detail', tenant, clinicNumber, patientId],
    refetchType: 'active',
  });
}

function assertDeleteSuccess(res: ApiResponse<unknown>): void {
  if (!res?.success) {
    const err: Error & { response?: { status: number; data: unknown } } = new Error(
      res?.message || CONSENT_DELETE_ERROR_FALLBACK
    );
    err.response = { status: 422, data: res };
    throw err;
  }
}

interface UseDeleteConsentMutationProps {
  patientId: string;
  consentId: string;
}

/** Edit consent screen: delete current record, toast, list invalidation, navigate to list. */
export function useDeleteConsentMutation({ patientId, consentId }: UseDeleteConsentMutationProps) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenant: tenantParam } = useParams();
  const toast = useAppToast();
  const hasHandledRef = useRef(false);

  return useMutation({
    mutationFn: async () => {
      try {
        const res = await deleteConsent(tenant, clinicNumber, patientId, consentId, serviceType);
        assertDeleteSuccess(res);
        return res;
      } catch (error: any) {
        // Idempotent delete: if already deleted (e.g. concurrent call / other tab), treat as success.
        if (error?.response?.status === 404) {
          return { success: true, data: null } as ApiResponse<unknown>;
        }
        throw error;
      }
    },
    onSuccess: response => {
      if (hasHandledRef.current) return;
      hasHandledRef.current = true;
      toast.success(consentDeleteSuccessToastMessage(response?.message));
      if (tenant && clinicNumber && patientId) {
        invalidateConsentListForPatient(queryClient, tenant, clinicNumber, patientId);
        invalidateConsentDetailForPatient(queryClient, tenant, clinicNumber, patientId);
        void invalidateVisitClinicScheduleForPatient(queryClient, patientId);
      }
      queryClient.removeQueries({
        queryKey: ['consents', 'detail', tenant, clinicNumber, patientId, consentId, serviceType],
      });
      const routeTenant = tenantParam || `${tenant}/${clinicNumber}`;
      authorizeNavigation();
      navigate(TENANT_ROUTES.CONSENTS.LIST(routeTenant, patientId));
    },
    onError: (_error: unknown) => {
      if (hasHandledRef.current) return;
      toast.error(CONSENT_DELETE_ERROR_FALLBACK);
    },
  });
}

/**
 * Consent list page: delete by row id. On success invalidates list (refetch) and drops detail cache.
 */
export function useConsentDelete(patientId?: string) {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();

  return useMutation({
    mutationFn: async (id: number) => {
      try {
        const res = await deleteConsent(
          tenant! || '',
          clinicNumber! || '',
          patientId || '',
          String(id),
          serviceType
        );
        assertDeleteSuccess(res);
        return res;
      } catch (error: any) {
        // Idempotent delete for list page (multi-tab / concurrent safe)
        if (error?.response?.status === 404) {
          return { success: true, data: null } as ApiResponse<unknown>;
        }
        throw error;
      }
    },
    onSuccess: (_, deletedId) => {
      if (tenant && clinicNumber && patientId) {
        invalidateConsentListForPatient(queryClient, tenant, clinicNumber, patientId);
        invalidateConsentDetailForPatient(queryClient, tenant, clinicNumber, patientId);
        void invalidateVisitClinicScheduleForPatient(queryClient, patientId);
      }
      queryClient.removeQueries({
        queryKey: ['consents', 'detail', tenant, clinicNumber, patientId, String(deletedId), serviceType],
      });
    },
  });
}
